import { ArrowLeft, GripVertical, Package, Plus, Search, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { useCategory } from '../hooks/useCategories';
import { useProducts, useProductSearch } from '../hooks/useProducts';
import { CategoryService } from '../services/categoryService';
import { Product } from '../types/product';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';

export default function CategoryProductsPage() {
    const { categoryId } = useParams<{ categoryId: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

    const { data: category, refetch: refetchCategory, isLoading: isCategoriesLoading } = useCategory(categoryId ?? '');
    // Fetch all products for rendering the category's existing product list
    const { products: allProducts, isLoading: isAllProductsLoading } = useProducts(1, 1000);
    // Fetch searched products (keep this loading local to the search area to avoid input unmounting)
    const { data: searchedProducts, isLoading: isSearchLoading } = useProductSearch(debouncedSearchQuery);

    // Debounce search query to prevent excessive API calls and focus loss
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery]);



    const handleAddProduct = async (productId: string) => {
        if (!category) return;

        // Check if product is already in this category
        if (category.productIds?.includes(productId)) {
            toast.error('This product is already in the category');
            return;
        }

        try {
            await CategoryService.addProductToCategory(category.id, productId);
            refetchCategory();

            toast.success('Product added to category');
        } catch (error) {
            toast.error('Failed to add product');
        }
    };

    const handleRemoveProduct = async (productId: string) => {
        if (!category) return;

        try {
            await CategoryService.removeProductFromCategory(category.id, productId);
            refetchCategory();

            toast.success('Product removed from category');
        } catch (error) {
            toast.error('Failed to remove product');
        }
    };

    const handleDragEnd = async (result: DropResult) => {
        if (!result.destination || !category) return;
        if (result.destination.index === result.source.index) return;

        // Drag indices refer to the rendered list, which skips IDs that don't resolve to a
        // loaded product, so reorder that list and keep any unresolved IDs at the end.
        const productIds = category.productIds || [];
        const visibleIds = productIds.filter(id => getProductById(id));
        const hiddenIds = productIds.filter(id => !getProductById(id));
        const [reorderedItem] = visibleIds.splice(result.source.index, 1);
        visibleIds.splice(result.destination.index, 0, reorderedItem);
        const items = [...visibleIds, ...hiddenIds];

        // Apply the new order right away so the row doesn't snap back while the request runs
        const queryKey = ['category', category.id];
        queryClient.setQueryData(queryKey, { ...category, productIds: items });

        try {
            await CategoryService.reorderCategoryProducts(category.id, items);
        } catch (error) {
            queryClient.setQueryData(queryKey, category);
            toast.error('Failed to reorder products');
        } finally {
            refetchCategory();
        }
    };

    const getProductById = (productId: string): Product | undefined => {
        return allProducts?.find(p => p._id === productId);
    };

    // Only block the page on initial category/all-products fetch.
    // Do NOT block on search fetching, otherwise the input unmounts and loses focus.
    if (isCategoriesLoading || isAllProductsLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!category) {
        return (
            <div className="container mx-auto p-6 max-w-4xl">
                <div className="text-center py-20">
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900">Category not found</h2>
                    <p className="text-gray-500 mt-2">The category you're looking for doesn't exist.</p>
                    <Button onClick={() => navigate('/categories')} className="mt-6">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Categories
                    </Button>
                </div>
            </div>
        );
    }

    const categoryProducts = (category.productIds || [])
        .map(id => getProductById(id))
        .filter((p): p is Product => p !== undefined);

    return (
        <div className="min-h-screen bg-slate-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-10">
                {/* Header */}
                <div className="flex items-start gap-4">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate('/categories')}
                        className="shrink-0 text-slate-600 hover:text-slate-900 hover:bg-white/70"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-4">
                    {category.imageUrl && (
                        <img
                            src={category.imageUrl}
                            alt={category.name}
                                    className="w-11 h-11 rounded-xl object-cover shrink-0 ring-1 ring-slate-200"
                        />
                    )}
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight truncate">
                                    {category.name}
                                </h1>
                                <p className="text-slate-500 text-sm mt-1">
                                    {categoryProducts.length} product{categoryProducts.length !== 1 ? 's' : ''} in this category
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add Products Section */}
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-medium text-slate-700">Add products</p>
                        <p className="text-xs text-slate-500">Search and add to this category</p>
                    </div>

                    <div className="max-w-lg">
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                placeholder="Search products to add..."
                                className="pl-10 h-10 text-sm bg-white/70 border-slate-200 focus:border-slate-300 focus:ring-0 placeholder:text-slate-400"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-white/70"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Search Results */}
                    {debouncedSearchQuery && (
                        <div className="mt-4">
                            {isSearchLoading ? (
                                <div className="py-3 text-sm text-slate-500">Searching…</div>
                            ) : !searchedProducts || searchedProducts.length === 0 ? (
                                <div className="py-6 text-center">
                                    <Package className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                                    <h3 className="text-sm font-medium text-slate-900 mb-1">No products found</h3>
                                    <p className="text-slate-500 text-xs">Try a different search term</p>
                                </div>
                            ) : (
                                <div className="max-h-80 overflow-auto pr-1">
                                    {searchedProducts.map(product => {
                                        const isInCurrentCategory = category?.productIds?.includes(product._id);

                                        return (
                                            <div
                                                key={product._id}
                                                className="flex items-center gap-4 px-2 py-3 rounded-lg hover:bg-white/70 transition-colors"
                                            >
                                                <img
                                                    src={product.images?.[0] || ''}
                                                    alt=""
                                                    className="w-11 h-11 rounded-lg object-cover bg-slate-100 shrink-0 ring-1 ring-slate-200"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-slate-900 truncate">{product.title}</p>
                                                    <div className="flex items-center gap-3 mt-1">
                                                    {isInCurrentCategory && (
                                                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-white/80 text-slate-600 ring-1 ring-slate-200">
                                                                Already added
                                                            </span>
                                                    )}
                                                    </div>
                                                </div>
                                                {isInCurrentCategory ? (
                                                    <div className="shrink-0">
                                                        <span className="text-slate-500 text-sm">Added</span>
                                                    </div>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleAddProduct(product._id)}
                                                        className="shrink-0 bg-white text-slate-900 border border-slate-200 hover:bg-slate-50"
                                                    >
                                                        <Plus className="w-4 h-4 mr-1" />
                                                        Add
                                                    </Button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Current Products */}
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <Package className="w-5 h-5 text-slate-400" />
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900">Products in Category</h2>
                                <p className="text-slate-500 text-sm">
                                    {categoryProducts.length} product{categoryProducts.length !== 1 ? 's' : ''}
                                </p>
                            </div>
                        </div>
                        {categoryProducts.length > 1 && (
                            <span className="text-xs text-slate-500 hidden sm:inline">Drag to reorder</span>
                        )}
                    </div>

                    {categoryProducts.length === 0 ? (
                        <div className="py-16 text-center">
                            <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-slate-900 mb-2">No products yet</h3>
                            <p className="text-slate-500 text-sm max-w-sm mx-auto">
                                Use the search box above to find and add products to this category.
                            </p>
                        </div>
                    ) : (
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="category-products">
                                {(provided) => (
                                    <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                    >
                                        {categoryProducts.map((product, index) => (
                                            <Draggable key={product._id} draggableId={product._id} index={index}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        className={cn(
                                                            "flex items-center gap-4 px-2 py-3 rounded-lg hover:bg-white/70 transition-colors",
                                                            snapshot.isDragging && "bg-white/70"
                                                        )}
                                                    >
                                                        <div
                                                            {...provided.dragHandleProps}
                                                            className="text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing shrink-0"
                                                        >
                                                            <GripVertical className="w-4 h-4" />
                                                        </div>
                                                        <div className="w-8 h-8 rounded-lg bg-white/80 ring-1 ring-slate-200 text-slate-600 text-sm font-medium flex items-center justify-center shrink-0">
                                                            {index + 1}
                                                        </div>
                                                        <img
                                                            src={product.images?.[0] || ''}
                                                            alt=""
                                                            className="w-11 h-11 rounded-lg object-cover bg-slate-100 shrink-0 ring-1 ring-slate-200"
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-medium text-slate-900 truncate">{product.title}</p>
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="text-slate-400 hover:text-slate-600 hover:bg-white/70 shrink-0"
                                                            onClick={() => handleRemoveProduct(product._id)}
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )}
                </div>
            </div>
        </div>
    );
}

